import { animationTime } from "../constants";

const MODAL_CLASSNAME = "js-modal";
const MODAL_CLOSE_BUTTON_CLASSNAME = `${MODAL_CLASSNAME}-close`;
const MODAL_TITLE_CLASSNAME = `${MODAL_CLASSNAME}-title`;
const MODAL_MESSAGE_CLASSNAME = `${MODAL_CLASSNAME}-message`;
const MODAL_CANCEL_BUTTON_CLASSNAME = `${MODAL_CLASSNAME}-cancel`;
const MODAL_OK_BUTTON_CLASSNAME = `${MODAL_CLASSNAME}-ok`;

class Modal {
  $body = null;
  $modalElement = null;
  $modalCloseButton = null;
  $modalTitleElement = null;
  $modalMessageElement = null;
  $modalCancelButton = null;
  $modalOKButton = null;
  currentModalId = "";
  positiveCallbackFunction = null;
  negativeCallbackFunction = null;
  modalDisplayedClassName = "is-displayed";
  customBodyClassName = "";
  modalAnimationTime = animationTime;
  customModalHandling = false;

  static modalDisplayedEventName = "ModalDisplayed";
  static modalClosedEventName = "ModalClosed";

  static attach() {
    const $modalElements = document.querySelectorAll(`.${MODAL_CLASSNAME}`);
    [...$modalElements].forEach(($modalElement) => {
      /* eslint-disable-next-line no-unused-vars */
      const modal = new Modal($modalElement);
    });
  }

  constructor($modalElement) {
    this.$body = document.body;
    this.$modalElement = $modalElement;
    this.$modalCloseButton = this.$modalElement.querySelector(`.${MODAL_CLOSE_BUTTON_CLASSNAME}`);
    this.$modalCancelButton = this.$modalElement.querySelector(`.${MODAL_CANCEL_BUTTON_CLASSNAME}`);
    this.$modalOKButton = this.$modalElement.querySelector(`.${MODAL_OK_BUTTON_CLASSNAME}`);
    this.$modalTitleElement = this.$modalElement.querySelector(`.${MODAL_TITLE_CLASSNAME}`);
    this.$modalMessageElement = this.$modalElement.querySelector(`.${MODAL_MESSAGE_CLASSNAME}`);

    const { modalId } = this.$modalElement.dataset;
    this.currentModalId = modalId || "";

    this.bindEvents();
  }

  bindEvents() {
    const buttonClickHandler = (isPositiveResponse) => {
      document.dispatchEvent(
        new CustomEvent(Modal.modalClosedEventName, {
          detail: {
            dispatchInfo: {
              modalId: this.currentModalId
            },
            isPositiveResponse
          }
        })
      );
    };

    if (this.$modalOKButton) {
      this.$modalOKButton.addEventListener("click", (e) => {
        e.preventDefault();
        buttonClickHandler(true);
      });
    }
    if (this.$modalCancelButton) {
      this.$modalCancelButton.addEventListener("click", (e) => {
        e.preventDefault();
        buttonClickHandler(false);
      });
    }
    if (this.$modalCloseButton) {
      this.$modalCloseButton.addEventListener("click", (e) => {
        e.preventDefault();
        buttonClickHandler(false);
      });
    }
    document.addEventListener(Modal.modalDisplayedEventName, (e) => {
      const {
        dispatchInfo,
        modalDisplayedCallbackFunc,
        positiveCallbackFunc,
        negativeCallbackFunc
      } = e.detail;
      const { modalId } = dispatchInfo;

      if (this.isMatchingModal(modalId)) {
        this.customBodyClassName = dispatchInfo.customBodyClass ? dispatchInfo.customBodyClass : "";
        this.customModalHandling = Modal.isFunctionType(modalDisplayedCallbackFunc);
        if (this.customModalHandling) {
          // NOTE: do custom handling here
          this.customModalHandling = true;
          modalDisplayedCallbackFunc({
            dispatchInfo,
            modalDisplayedCallbackFunc,
            positiveCallbackFunc,
            negativeCallbackFunc,
            modalElement: this.$modalElement
          });
        } else {
          if (this.$modalTitleElement) {
            this.$modalTitleElement.innerText = dispatchInfo.title || "";
          }
          if (this.$modalMessageElement) {
            this.$modalMessageElement.innerText = dispatchInfo.message || "";
          }
        }
        this.positiveCallbackFunction = Modal.isFunctionType(positiveCallbackFunc)
          ? () => {
              positiveCallbackFunc({
                dispatchInfo,
                modalElement: this.$modalElement
              });
            }
          : null;
        this.negativeCallbackFunction = Modal.isFunctionType(negativeCallbackFunc)
          ? () => {
              negativeCallbackFunc({
                dispatchInfo,
                modalElement: this.$modalElement
              });
            }
          : null;
        this.$modalElement.classList.add(this.modalDisplayedClassName);
        this.$body.classList.add("show-overlay");
        if (this.customBodyClassName) {
          this.$body.classList.add(this.customBodyClassName);
        }
      }
    });
    document.addEventListener(Modal.modalClosedEventName, (e) => {
      const { dispatchInfo, isPositiveResponse } = e.detail;
      const { modalId } = dispatchInfo;
      if (this.isMatchingModal(modalId)) {
        this.$modalElement.classList.remove(this.modalDisplayedClassName);
        this.$body.classList.remove("show-overlay");
        if (this.customBodyClassName) {
          this.$body.classList.remove(this.customBodyClassName);
        }
        this.customBodyClassName = "";

        setTimeout(() => {
          if (!this.customModalHandling) {
            if (this.$modalTitleElement) {
              this.$modalTitleElement.innerText = "";
            }
            if (this.$modalMessageElement) {
              this.$modalMessageElement.innerText = "";
            }
          }
        }, this.modalAnimationTime);

        /* eslint-disable no-lonely-if */
        if (isPositiveResponse) {
          if (
            this.positiveCallbackFunction &&
            typeof this.positiveCallbackFunction === "function"
          ) {
            this.positiveCallbackFunction();
          }
        } else {
          if (
            this.negativeCallbackFunction &&
            typeof this.negativeCallbackFunction === "function"
          ) {
            this.negativeCallbackFunction();
          }
        }
        /* eslint-enable no-lonely-if */
      }
    });
  }

  isMatchingModal(modalId) {
    if (!modalId && !this.currentModalId) {
      // targeting general modal without ID
      return true;
    }
    if (modalId === this.currentModalId) {
      // targeting modal with ID
      return true;
    }
    return false;
  }

  static isFunctionType(callback) {
    return callback && typeof callback === "function";
  }

  static triggerEvent(
    dispatchInfo,
    positiveCallbackFunc,
    negativeCallbackFunc,
    modalDisplayedCallbackFunc
  ) {
    const event = new CustomEvent(Modal.modalDisplayedEventName, {
      detail: {
        dispatchInfo,
        modalDisplayedCallbackFunc,
        positiveCallbackFunc,
        negativeCallbackFunc
      }
    });
    document.dispatchEvent(event);
  }
}

export { Modal };
